import { useCallback, useMemo } from "react";
import { usePostHog } from "posthog-js/react";
import type { ActivationStepReachedEvent } from "@/lib/analytics/activationJourney";

/**
 * Custom analytics hook that wraps PostHog with type-safe event tracking
 * Provides methods for tracking user interactions, page views, and custom events
 */

// Electron's preload script exposes electronAPI synchronously via contextBridge
// before any renderer JS executes. Safe to check at module load time.
const PLATFORM: "web" | "desktop" =
  typeof window !== "undefined" && "electronAPI" in window ? "desktop" : "web";

const COMMON_METADATA = {
  platform: PLATFORM,
} as const;

// Define all tracked events with their properties for type safety
export interface AnalyticsEvents {
  // Surface view events shared with mobile (pages vs. screens)
  surface_viewed: {
    surface: string;
    surface_type: "page" | "screen";
    title?: string;
    referrer?: string;
  };

  // Authentication events
  auth_succeeded: {
    method: "email" | "google" | "apple" | "github";
  };
  auth_failed: {
    method: "email" | "google" | "apple" | "github";
    error?: string;
  };
  user_signed_out: Record<string, never>;

  activation_step_reached: ActivationStepReachedEvent;

  // Chat and Voice Interaction Events
  message_sent: {
    session_id: string;
    message_type: "text" | "voice";
    message_length?: number;
  };

  // App lifecycle events (mobile only, but defined here for type consistency)
  app_opened: {
    source?: "cold_start" | "background" | "notification";
  };
  voice_session_started: {
    session_id: string;
    trigger: "button" | "keyboard_shortcut";
  };
  voice_session_ended: {
    session_id: string;
    reason?: "leave" | "disconnect";
  };

  // Desktop onboarding funnel -- each step is a distinct event for clean PostHog funnels.
  // Only emitted from the desktop app.
  desktop_cli_setup_shown: {
    is_first_launch: boolean;
  };
  desktop_cli_installed: {
    tool: "omnara" | "claude" | "codex";
    duration_ms?: number;
  };
  desktop_cli_install_failed: {
    tool: "omnara" | "claude" | "codex";
    error: string;
    duration_ms?: number;
  };
  desktop_cli_auth_started: {
    tool: "claude" | "codex";
    provider: "claudeai" | "console" | "openai" | "api-key";
  };
  desktop_cli_auth_completed: {
    tool: "claude" | "codex";
    provider?: "claudeai" | "console" | "openai" | "api-key";
    duration_ms?: number;
  };
  desktop_cli_auth_failed: {
    tool: "claude" | "codex";
    error: string;
    duration_ms?: number;
  };
  desktop_daemon_started: Record<string, never>;
  desktop_onboarding_completed: {
    any_agent_ready: boolean;
    daemon_running?: boolean;
    intent?: "add-workspace" | "done" | "later";
  };

  // Fired when the user's attempt to add a workspace via the desktop
  // onboarding flow fails. `reason` classifies the failure so we can build
  // funnels around macOS TCC denials vs. other backend errors. We do NOT
  // ship raw error messages or paths -- those often contain the user's
  // filesystem layout (effectively PII) and we only need the coarse
  // classification to answer "how many new users get blocked by TCC".
  workspace_add_failed: {
    reason: "permission_denied" | "timeout" | "other";
    // Coarse label for the offending path's root directory.
    path_root?: string;
    // HTTP status returned by the backend, when available.
    status?: number;
  };

  cloud_sandbox_setup_started: {
    workspace_id: string;
    missing_reason: string;
  };
  cloud_sandbox_setup_completed: {
    workspace_id: string;
  };
  cloud_sandbox_setup_dismissed: {
    workspace_id: string;
  };
}

export function useAnalytics() {
  const posthog = usePostHog();
  type TrackFn = <K extends keyof AnalyticsEvents>(
    eventName: K,
    properties?: AnalyticsEvents[K]
  ) => void;

  /**
   * Track a specific event with type-safe properties
   * Automatically includes common metadata (platform, viewport, etc.)
   */
  const track = useCallback<TrackFn>(
    (eventName, properties) => {
      if (!posthog) {
        // PostHog not initialized (e.g., in development without key)
        return;
      }

      const sessionId =
        typeof posthog.get_session_id === "function" ? posthog.get_session_id() : "";
      const enrichedProperties = {
        ...COMMON_METADATA,
        ...properties,
        ...(sessionId ? { $session_id: sessionId } : {}),
      };

      try {
        posthog.capture(eventName, enrichedProperties);
      } catch {
        // Silently handle tracking failures
      }
    },
    [posthog]
  );

  /**
   * Identify a user with their properties
   */
  const identify = useCallback(
    (userId: string, properties?: Record<string, unknown>) => {
      if (!posthog) {
        return;
      }

      try {
        posthog.identify(userId, properties);
      } catch {
        // Silently handle identify failures
      }
    },
    [posthog]
  );

  /**
   * Reset the current user (on sign out)
   */
  const reset = useCallback(() => {
    if (!posthog) {
      return;
    }

    try {
      posthog.reset();
    } catch {
      // Silently handle reset failures
    }
  }, [posthog]);

  /**
   * Track a page view (shares the surface_viewed event with mobile)
   */
  const trackPageView = useCallback(
    (path: string, title?: string) => {
      track("surface_viewed", {
        surface: path,
        surface_type: "page",
        title: title || document.title,
        referrer: document.referrer || undefined,
      });
    },
    [track]
  );

  /**
   * Track a chat message
   */
  const trackChatMessage = useCallback(
    (sessionId: string, isVoice: boolean = false, messageLength?: number) => {
      track("message_sent", {
        session_id: sessionId,
        message_type: isVoice ? "voice" : "text",
        message_length: messageLength,
      });
    },
    [track]
  );

  return useMemo(
    () => ({
      track,
      identify,
      reset,
      trackPageView,
      trackChatMessage,
      // Export the raw posthog instance for advanced usage
      posthog,
    }),
    [track, identify, reset, trackPageView, trackChatMessage, posthog]
  );
}

export default useAnalytics;
